import { Box, Button, Chip } from '@mui/material'
import { useEffect, useState } from 'react'
import { useMessageSource } from 'src/i18n/useMessageSource'
import {
  ClassificationCategoryAndScores,
  fetchFactsheetClassificationCategories,
} from 'src/screens/factsheets/factsheet/FactsheetEffectivenessClassificationUtils'
import { FilterFieldWrapper } from 'src/screens/shared/common/filter-card/FilterFieldWrapper'
import { AddEffectivenessClassificationsModalDialog } from 'src/screens/shared/common/filter-card/filters/effectiveness-classifications/AddEffectivenessClassificationsModalDialog'
import {
  EffectivenessClassificationFilterItem,
  EffectivenessClassificationsFilterModel,
} from 'src/shared/constants/filter-constants'
import { FEATURE_OPERATOR, FEATURE_OPERATOR_TYPE } from 'src/shared/constants/reporting-constants'
import { AddCircleIcon } from 'src/shared/icons/Icons'
import { ModalDialog } from 'src/shared/modal-dialog/ModalDialog'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useModalCancel } from 'src/shared/utils/hooks/modal-hooks'
import { useUserLocale } from 'src/user/UserContext'
import styled from 'styled-components'
import { useClient } from 'urql'

interface Props {
  setEffectivenessClassificationsInput: (v: EffectivenessClassificationsFilterModel) => void
  isReset?: boolean
  initialState?: EffectivenessClassificationsFilterModel
}

const BoxStyled = styled(Box)`
  .MuiChip-root {
    margin-block: ${({ theme }) => theme.spacing(0.25)};
    margin-right: ${({ theme }) => theme.spacing(0.5)};
  }

  .MuiButton-root {
    height: 32px;
  }
`

export const EffectivenessClassificationsFilter = ({
  setEffectivenessClassificationsInput,
  isReset,
  initialState,
}: Props) => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const language = useUserLocale()
  const notificationService = useNotificationService()

  const [categories, setCategories] = useState<ClassificationCategoryAndScores[]>([])
  const [selectedEffectivenessClassifications, setSelectedEffectivenessClassifications] = useState<
    EffectivenessClassificationFilterItem[]
  >([])
  const [selectedOperator, setSelectedOperator] = useState<FEATURE_OPERATOR_TYPE>(FEATURE_OPERATOR.AND)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (initialState) {
      setSelectedEffectivenessClassifications(initialState.selectedEffectivenessClassifications)
      setSelectedOperator(initialState.withinCategoryOperator)
    }
  }, [initialState])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const classificationCategories = await fetchFactsheetClassificationCategories(urqlClient, language)
        setCategories(classificationCategories)
      } catch {
        notificationService.operationFailed()
      }
    }

    fetchData()
  }, [urqlClient, language, notificationService])

  useEffect(() => {
    setEffectivenessClassificationsInput({
      withinCategoryOperator: selectedOperator,
      betweenCategoriesOperator: selectedOperator,
      selectedEffectivenessClassifications,
    })
  }, [selectedEffectivenessClassifications, selectedOperator, setEffectivenessClassificationsInput])

  useEffect(() => {
    if (isReset) {
      setSelectedEffectivenessClassifications([])
      setSelectedOperator(FEATURE_OPERATOR.AND)
    }
  }, [isReset])

  const resolveSelectionLabel = (selection: EffectivenessClassificationFilterItem): string => {
    const category = categories.find((item) => item.categoryId === selection.categoryId)
    const score = category?.scores.find((item) => item.value === selection.scoreId)

    return `${category?.categoryName ?? selection.categoryId}: ${score?.label ?? selection.scoreId}`
  }

  const handleOpenModal = () => setModalOpen(true)

  const handleCloseModal = useModalCancel(() => setModalOpen(false))

  const handleDelete = (selectionToDelete: EffectivenessClassificationFilterItem) => () => {
    setSelectedEffectivenessClassifications((currentSelections) =>
      currentSelections.filter(
        (selection) =>
          !(selection.categoryId === selectionToDelete.categoryId && selection.scoreId === selectionToDelete.scoreId),
      ),
    )
  }

  const handleConfirmAddEffectivenessClassifications = (
    values: EffectivenessClassificationFilterItem[],
    operator: FEATURE_OPERATOR_TYPE,
  ) => {
    setSelectedEffectivenessClassifications(values)
    setSelectedOperator(operator)
    setModalOpen(false)
  }

  const resolveOperatorKey = (operator: FEATURE_OPERATOR_TYPE): string =>
    getMessage(`label.operator.${operator}`).toUpperCase()

  return (
    <>
      <FilterFieldWrapper label={getMessage('label.factsheet.edit.evidence_of_effectiveness.classification.title')}>
        <BoxStyled>
          {selectedEffectivenessClassifications.map((selection) => (
            <Chip
              key={`${selection.categoryId}_${selection.scoreId}`}
              label={resolveSelectionLabel(selection)}
              onDelete={handleDelete(selection)}
              color="primary"
            />
          ))}
          {selectedEffectivenessClassifications.length > 0 && (
            <Chip label={resolveOperatorKey(selectedOperator)} color="primary" />
          )}
          <Button variant="text" startIcon={<AddCircleIcon />} onClick={handleOpenModal}>
            {getMessage('button.add')}
          </Button>
        </BoxStyled>
      </FilterFieldWrapper>
      <ModalDialog
        open={modalOpen}
        onClose={handleCloseModal}
        title={getMessage('label.factsheet.edit.evidence_of_effectiveness.classification.title')}
        maxWidth="md"
      >
        <AddEffectivenessClassificationsModalDialog
          categories={categories}
          onCancel={handleCloseModal}
          onConfirm={handleConfirmAddEffectivenessClassifications}
          selectedEffectivenessClassifications={selectedEffectivenessClassifications}
          selectedOperator={selectedOperator}
        />
      </ModalDialog>
    </>
  )
}
